import { useEffect, useState } from 'react'
import { FileUp, HelpCircle, Sparkles } from 'lucide-react'
import { api, type ImportPreview, type ImportRow } from '../lib/api.ts'
import { useRouter } from '../lib/router.tsx'
import { Shell } from '../components/Shell.tsx'
import { Button } from '../components/ui/button.tsx'
import { Card, CardContent } from '../components/ui/card.tsx'
import { Input, Label, Select } from '../components/ui/input.tsx'
import { Alert } from '../components/ui/alert.tsx'
import { Badge } from '../components/ui/badge.tsx'
import { useToast } from '../components/ui/toast.tsx'
import { cn, plural } from '../lib/utils.ts'
import type { Theme } from '../lib/theme.ts'
import { useAdminMode } from '../lib/useAdminMode.tsx'
import { AdminNotice } from '../components/AdminNotice.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'

/**
 * Import kánonu z PDF. Server umí vytáhnout autory a názvy a doplnit fakta
 * o dílech, která už zná ze znalostní báze — zbytek dopíše uživatel tady.
 * Obohacení modelem běží jen v CLI, aby produkce nedržela API klíč.
 */
export function ImportPage({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const { navigate } = useRouter()
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [rows, setRows] = useState<ImportRow[]>([])
  const [name, setName] = useState('')
  const [id, setId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { locked } = useAdminMode()
  const toast = useToast()
  // Commit existující kánon přepíše — díla se smažou a nahradí. Aby se na to
  // dalo upozornit dopředu, drží se tu seznam už existujících id.
  const [existing, setExisting] = useState<{ id: string; name: string }[]>([])
  const [confirmOverwrite, setConfirmOverwrite] = useState(false)

  useEffect(() => {
    api.canons().then(setExisting).catch(() => setExisting([]))
  }, [])

  async function upload(file: File) {
    setBusy(true)
    setError(null)
    try {
      const p = await api.importPreview(file)
      setPreview(p)
      setRows(p.rows)
      setName(p.suggestedName)
      setId(p.suggestedName.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import se nepodařil')
    } finally {
      setBusy(false)
    }
  }

  const incomplete = rows.filter((r) => !r.formId || !r.origin)

  const collision = existing.find((c) => c.id === id)

  function requestCommit() {
    if (collision) setConfirmOverwrite(true)
    else void commit()
  }

  async function commit() {
    setBusy(true)
    setError(null)
    try {
      const result = await api.importCommit({
        id,
        name,
        rows: rows.map((r) => ({
          n: r.n, author: r.author, title: r.title, sourcePage: r.sourcePage,
          formId: r.formId ?? 'prose', firstPublished: r.firstPublished,
          origin: r.origin ?? 'world', originalLanguage: r.originalLanguage,
        })),
      })
      navigate('/')
      toast(`Kánon „${result.name}" uložen — ${result.works} děl.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uložení se nepodařilo')
    } finally {
      setBusy(false)
    }
  }

  const patchRow = (n: number, patch: Partial<ImportRow>) =>
    setRows((prev) => prev.map((r) => (r.n === n ? { ...r, ...patch } : r)))

  return (
    <Shell
      theme={theme}
      onToggleTheme={onToggleTheme}
      title="Import kánonu z PDF"
      subtitle="Nahraj seznam děl od školy. Co už známe z dřívějších importů, se doplní samo."
    >
      <AdminNotice what="Uložení kánonu" />
      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}

      {!preview && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center sm:py-14">
            <FileUp size={30} className="text-[var(--muted-foreground)]" aria-hidden />
            <p className="text-[var(--muted-foreground)]">PDF musí mít textovou vrstvu — sken bez textu parser nepřečte.</p>
            <Button asChild disabled={busy}>
              <label className="cursor-pointer">
                {busy ? 'Zpracovávám…' : 'Vybrat PDF'}
                <input type="file" accept="application/pdf" className="sr-only" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              </label>
            </Button>
          </CardContent>
        </Card>
      )}

      {preview && (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="canon-name">Název kánonu</Label>
              <Input id="canon-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="canon-id">Identifikátor</Label>
              <Input id="canon-id" value={id} onChange={(e) => setId(e.target.value)} pattern="[a-z0-9-]+" />
            </div>
          </div>

          <Alert className="mb-4">
            <Sparkles size={16} aria-hidden className="text-[var(--muted-foreground)]" />
            <p className="flex-1">
              Nalezeno {plural(preview.total, 'dílo', 'díla', 'děl')}, z toho <strong>{preview.known}</strong> doplněno ze
              znalostní báze a <strong>{preview.missing}</strong> čeká na doplnění.
              {incomplete.length > 0 && ' Import lze potvrdit i s neúplnými řádky — doplnit je jde později.'}
            </p>
          </Alert>

          <div className="overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)]">
            {/* Šest sloupců s inputy se na mobil nevejde a schovat je nejde —
                editují se. Necháme je tedy rolovat do stran. */}
            <div className="max-h-[min(34rem,calc(100dvh-18rem))] overflow-auto">
              <table className="w-full min-w-[44rem] text-left text-[length:var(--text-sm)]">
                <caption className="sr-only">Náhled importovaných děl</caption>
                <thead className="sticky top-0 bg-[var(--card)]">
                  <tr className="border-b border-[var(--border)] text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
                    <th scope="col" className="w-12 px-3 py-2">#</th>
                    <th scope="col" className="px-3 py-2">Autor</th>
                    <th scope="col" className="px-3 py-2">Název</th>
                    <th scope="col" className="px-3 py-2">Forma</th>
                    <th scope="col" className="px-3 py-2">Rok</th>
                    <th scope="col" className="px-3 py-2">Původ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.n}
                      data-confidence={r.known ? 'high' : 'low'}
                      className={cn(
                        'border-b border-[var(--border)] last:border-0',
                        !r.known && 'bg-[var(--import-uncertain-surface)] shadow-[inset_3px_0_0_var(--import-uncertain-border)]',
                      )}
                    >
                      <td className="px-3 py-1.5 text-[var(--muted-foreground)]">
                        <span className="tabular inline-flex items-center gap-1">
                          {r.n}
                          {!r.known && <HelpCircle size={12} className="text-[var(--import-uncertain)]" aria-label="vyžaduje kontrolu" />}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        <Input inputSize="sm" value={r.author ?? ''} aria-label={`Autor díla ${r.n}`}
                          onChange={(e) => patchRow(r.n, { author: e.target.value || null })} />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input inputSize="sm" value={r.title} aria-label={`Název díla ${r.n}`}
                          onChange={(e) => patchRow(r.n, { title: e.target.value })} />
                      </td>
                      <td className="px-3 py-1.5">
                        <Select inputSize="sm" className={cn(!r.formId && 'border-[var(--import-uncertain-border)]')} value={r.formId ?? ''} aria-label={`Forma díla ${r.n}`}
                          onChange={(e) => patchRow(r.n, { formId: (e.target.value || null) as ImportRow['formId'] })}>
                          <option value="">doplnit</option>
                          <option value="prose">Próza</option>
                          <option value="poetry">Poezie</option>
                          <option value="drama">Drama</option>
                        </Select>
                      </td>
                      <td className="px-3 py-1.5">
                        <Input inputSize="sm" className="w-24" type="number" value={r.firstPublished ?? ''} aria-label={`Rok vydání díla ${r.n}`}
                          onChange={(e) => patchRow(r.n, { firstPublished: e.target.value === '' ? null : Number(e.target.value) })} />
                      </td>
                      <td className="px-3 py-1.5">
                        <Select inputSize="sm" className={cn(!r.origin && 'border-[var(--import-uncertain-border)]')} value={r.origin ?? ''} aria-label={`Původ díla ${r.n}`}
                          onChange={(e) => patchRow(r.n, { origin: (e.target.value || null) as ImportRow['origin'] })}>
                          <option value="">doplnit</option>
                          <option value="cs">česká</option>
                          <option value="world">světová</option>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Badge>{plural(rows.length, 'řádek', 'řádky', 'řádků')}</Badge>
            {incomplete.length > 0 && (
              <Badge className="border-[var(--import-uncertain-border)] bg-[var(--import-uncertain-surface)] text-[var(--import-uncertain)]">
                {incomplete.length} k doplnění
              </Badge>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" onClick={() => setPreview(null)}>Nahrát jiné PDF</Button>
              <Button onClick={requestCommit} disabled={busy || locked || !id || !name}>{busy ? 'Ukládám…' : 'Uložit kánon'}</Button>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOverwrite}
        onOpenChange={setConfirmOverwrite}
        title="Přepsat existující kánon?"
        description={
          <>
            Pod identifikátorem <strong>{id}</strong> už je kánon „{collision?.name}".
            Uložením se jeho díla <strong>smažou a nahradí</strong> těmito.
            Seznamy, které z něj vycházejí, přijdou o vybraná díla, která v novém kánonu nebudou.
            Jestli to nechceš, změň identifikátor.
          </>
        }
        confirmLabel="Přepsat kánon"
        destructive
        onConfirm={commit}
      />
    </Shell>
  )
}
